import type { IMedicalCompetence } from "@medoma/models/shifts";
import { useState } from "react";
import type { IActivityTemplate, IBatchTemplate } from "@/api/Templates";
import { filterTagsByGroup, parseCompetenceTagName } from "./tagUtils";
import { medicalCompetenceSchema } from "@medoma/models/shifts";
import { z } from "zod";
import { tagGroupsSchema } from "@medoma/models/templates";
import { userStorageKey } from "@medoma/shared-employee/userStorageKey";
import { useUserProfile } from "@/api/Users";

type ITemplate = IActivityTemplate | IBatchTemplate;

const readFromStorage = (key: string): IMedicalCompetence | null => {
  const stored = localStorage.getItem(key);
  if (!stored) {
    return null;
  }
  const parsed = medicalCompetenceSchema.safeParse(stored);
  return parsed.success ? parsed.data : null;
};

// `storageKey` is scoped to the signed-in employee: the filter decides which
// templates are offered, so one inherited from the previous employee on a
// shared computer hides templates from a list that looks complete.
export const useTemplateManagerCompetenceFilter = (storageKey?: string) => {
  const { userId } = useUserProfile();
  const scopedKey =
    storageKey === undefined
      ? undefined
      : userStorageKey({ userId, key: storageKey });

  const [filteredCompetence, setFilteredCompetence] =
    useState<IMedicalCompetence | null>(
      scopedKey ? readFromStorage(scopedKey) : null,
    );

  const selectCompetence = (competence: IMedicalCompetence | null) => {
    if (scopedKey) {
      if (competence === null) {
        localStorage.removeItem(scopedKey);
      } else {
        try {
          localStorage.setItem(scopedKey, competence);
        } catch {
          // QuotaExceededError or SecurityError (site data blocked). Fall back
          // to no filter rather than showing one we could not persist.
          localStorage.removeItem(scopedKey);
          setFilteredCompetence(null);
          return;
        }
      }
    }
    setFilteredCompetence(competence);
  };

  const clearAllFilters = () => {
    if (scopedKey) {
      localStorage.removeItem(scopedKey);
    }
    setFilteredCompetence(null);
  };

  const matchesCompetenceFilter = (template: ITemplate) => {
    if (filteredCompetence === null) {
      return true;
    }

    const competenceTags = filterTagsByGroup(
      template.tags,
      tagGroupsSchema.enum.competence,
    );

    if (competenceTags.length === 0) {
      return true;
    }

    const templateCompetences = z
      .array(medicalCompetenceSchema)
      .parse(
        competenceTags.map((tag) => parseCompetenceTagName(tag.displayName)),
      );

    return templateCompetences.includes(filteredCompetence);
  };

  return {
    filteredCompetence,
    selectCompetence,
    clearAllFilters,
    matchesCompetenceFilter,
  };
};
